/**
 * NotificationStackEffect creates a vertical notification stack where slides appear
 * stacked behind the top card, peeking downward at decreasing scales.
 *
 * The effect shows 3 slides visible:
 * - Top slide: 100% scale, center position, highest z-index (fully visible)
 * - Second slide: 92% scale, offset down 28px, medium z-index (peeks below top)
 * - Third slide: 83% scale, offset down 50px, lowest z-index (peeks below second)
 *
 * When the top card is dismissed, it blooms (scale grows), blurs, fades out,
 * and floats upward — like a notification that's been seen.
 */

// Factory function - class is created when TarotEffect is available
function createNotificationStackEffect(TarotEffect) {
	return class NotificationStackEffect extends TarotEffect {
		static effectName = 'notification-stack';

		static defaultOptions = {
			notificationStack: { blur: 20, scale: 0.25, yOffset: -40, stackOffset: 8 },
		};

		/**
		 * Effect configuration rules that define carousel behavior constraints
		 */
		static rules = {
			minSlidesPerView: 1,
			maxSlidesPerView: 1,
			loopBuffer: { left: 0, right: 3 },
		};

		/**
		 * Creates a new notification stack effect instance
		 * @param {Object} ctx - The shared context object
		 */
		constructor(ctx) {
			super(ctx);
			this.#loadOptions();
			this.init();
		}

		#loadOptions() {
			const opts = this.ctx.store.getOptions().notificationStack || {};
			this.blurAmount = opts.blur;
			this.scaleAmount = opts.scale;
			this.yOffset = opts.yOffset;
			this.stackOffset = opts.stackOffset;

			// Stack visual configuration
			this.topSlideBaseScale = 1;
			this.secondSlideRelativeScale = 0.95;
			this.thirdSlideRelativeScale = 0.88;
		}

		init() {
			super.init();
			this.applyStackSpacing();
		}

		reInit() {
			super.reInit();
			this.#loadOptions();
			this.applyStackSpacing();
		}

		/**
		 * Calculate total space needed for stack previews (peek reserve as bottom padding)
		 */
		applyStackSpacing() {
			const reserveSpace = this.stackOffset * 1.5 + 6;
			this.ctx.viewport.style.setProperty('--notification-stack-peek-reserve', `${reserveSpace}px`);
		}

		/**
		 * Main render function called every animation frame
		 *
		 * @param {Object} frame - Complete frame object with all carousel data
		 * @param {Object} utils - Frame utilities for position calculations
		 */
		render(frame, utils) {
			const _ = this;
			const { slides, widths } = frame;

			_.renderSlideWidth(widths.slide);

			for (let slideIndex = 0, totalSlides = slides.length; slideIndex < totalSlides; slideIndex++) {
				const slide = slides[slideIndex];

				const exitingCard = utils.isSlideInRange(slide, 'CL1', 'L1');
				if (exitingCard.isInRange) {
					_.applyExitingCardPosition(slide, exitingCard.percent);
					continue;
				}

				const topCard = utils.isSlideInRange(slide, 'R1', 'CL1');
				if (topCard.isInRange) {
					_.applyTopCardPosition(slide, topCard.percent);
					continue;
				}

				const secondCard = utils.isSlideInRange(slide, 'R2', 'R1');
				if (secondCard.isInRange) {
					_.applySecondCardPosition(slide, secondCard.percent);
					continue;
				}

				const thirdCard = utils.isSlideInRange(slide, 'R3', 'R2');
				if (thirdCard.isInRange) {
					_.applyThirdCardPosition(slide, thirdCard.percent);
					continue;
				}

				_.applyHiddenState(slide);
			}
		}

		/**
		 * Apply positioning for exiting card (CL1 -> L1)
		 * Card blooms (scale grows), blurs, fades out, and floats upward
		 *
		 * @param {HTMLElement} slide - The slide element to transform
		 * @param {number} percent - Progress (1 at CL1 start, 0 at L1 fully exited)
		 */
		applyExitingCardPosition(slide, percent) {
			const _ = this;

			if (percent > 0.995) percent = 1;
			if (percent < 0.005) percent = 0;

			const remaining = 1 - percent;

			// Bloom: scale grows from 1.0 to 1.0 + scaleAmount
			const scale = 1 + remaining * _.scaleAmount;

			// Blur increases as card exits
			const blur = remaining * _.blurAmount;

			// Float upward (negative Y)
			const ty = remaining * _.yOffset;

			// Fade out
			const opacity = percent;

			// Safari stacking context
			const translateZ = 0.004;

			slide.style.transform = `translate3d(0px, ${ty}px, ${translateZ}px) scale(${scale})`;
			slide.style.transformOrigin = 'center bottom';
			slide.style.filter = `blur(${blur}px)`;
			slide.style.zIndex = 4;
			slide.style.opacity = opacity;
			slide.show();
		}

		/**
		 * Apply positioning for the top card (R1 -> CL1)
		 * Transitions from resting top position toward second card position
		 *
		 * @param {HTMLElement} slide - The slide element to transform
		 * @param {number} percent - Progress (0 at CL1 resting, 1 at R1 transitioning)
		 */
		applyTopCardPosition(slide, percent) {
			const _ = this;

			if (percent > 0.995) percent = 1;
			if (percent < 0.005) percent = 0;

			// Interpolate scale: 1.0 -> secondSlideRelativeScale
			const startScale = _.topSlideBaseScale;
			const endScale = _.topSlideBaseScale * _.secondSlideRelativeScale;
			const scale = startScale + (endScale - startScale) * percent;

			// Interpolate Y offset: 0 -> stackOffset
			const offsetY = _.stackOffset * percent;

			const translateZ = 0.003;

			slide.style.transform = `translate3d(0px, ${offsetY}px, ${translateZ}px) scale(${scale})`;
			slide.style.transformOrigin = 'center bottom';
			slide.style.filter = 'blur(0px)';
			slide.style.zIndex = 3;
			slide.style.opacity = 1;
			slide.show();
		}

		/**
		 * Apply positioning for the second card (R2 -> R1)
		 * Transitions from second position to third position
		 *
		 * @param {HTMLElement} slide - The slide element to transform
		 * @param {number} percent - Progress (0 at R1 second position, 1 at R2 becoming third)
		 */
		applySecondCardPosition(slide, percent) {
			const _ = this;

			if (percent > 0.995) percent = 1;
			if (percent < 0.005) percent = 0;

			// Interpolate scale: secondSlideRelativeScale -> thirdSlideRelativeScale
			const startScale = _.topSlideBaseScale * _.secondSlideRelativeScale;
			const endScale = _.topSlideBaseScale * _.thirdSlideRelativeScale;
			const scale = startScale + (endScale - startScale) * percent;

			// Interpolate Y offset: stackOffset -> endY
			const startY = _.stackOffset;
			const endY = _.stackOffset * 2 - 2;
			const offsetY = startY + (endY - startY) * percent;

			const translateZ = 0.002;

			slide.style.transform = `translate3d(0px, ${offsetY}px, ${translateZ}px) scale(${scale})`;
			slide.style.transformOrigin = 'center bottom';
			slide.style.filter = 'blur(0px)';
			slide.style.zIndex = 2;
			slide.style.opacity = 1;
			slide.show();
		}

		/**
		 * Apply positioning for the third card (R3 -> R2)
		 * Card slides down into view from behind
		 *
		 * @param {HTMLElement} slide - The slide element to transform
		 * @param {number} percent - Progress (0 at R3 entering, 1 at R2 tucked behind)
		 */
		applyThirdCardPosition(slide, percent) {
			const _ = this;

			if (percent > 0.995) percent = 1;
			if (percent < 0.005) percent = 0;

			const scale = _.topSlideBaseScale * _.thirdSlideRelativeScale;

			// Slide from hidden (0px) to visible (stackOffset * 2) — inverted
			const offsetY = (_.stackOffset * 2 - 2) * (1 - percent);

			const translateZ = 0.001;

			slide.style.transform = `translate3d(0px, ${offsetY}px, ${translateZ}px) scale(${scale})`;
			slide.style.transformOrigin = 'center bottom';
			slide.style.filter = 'blur(0px)';
			slide.style.zIndex = 1;
			slide.style.opacity = 1;
			slide.show();
		}

		/**
		 * Hide slides that are outside the visible stack positions
		 *
		 * @param {HTMLElement} slide - The slide element to hide
		 */
		applyHiddenState(slide) {
			slide.style.opacity = 0;
			slide.hide();
			slide.style.zIndex = 0;
			slide.style.transform = 'translate3d(0, 0, 0) scale(1)';
			slide.style.transformOrigin = 'center center';
			slide.style.filter = '';
		}

		/**
		 * Clean up the effect when it's being destroyed or replaced
		 */
		destroy() {
			super.destroy();
			const _ = this;

			if (_.ctx.viewport && _.ctx.viewport.style) {
				_.ctx.viewport.style.removeProperty('--notification-stack-peek-reserve');
			}

			const slides = _.ctx.store.getSlides();
			for (let i = 0; i < slides.length; i++) {
				const slide = slides[i];
				slide.style.transform = '';
				slide.style.transformOrigin = '';
				slide.style.opacity = '';
				slide.style.zIndex = '';
				slide.style.filter = '';
				slide.show?.();
			}
		}
	};
}

// Queue the factory (works regardless of whether Tarot is loaded)
(window.TarotEffectQueue = window.TarotEffectQueue || []).push({
	effectName: 'notification-stack',
	factory: createNotificationStackEffect,
});

export default createNotificationStackEffect;
